import { sub } from 'date-fns';
import _ from 'lodash';
import { Store } from '@/core/flux.service';
import { VantageNames } from '@/vantage/constants';
import type { PersistenceLevel } from '@/core/flux.service';
import type { ViewDateRange } from '@/vantage/types/DateTime.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import type { GridState } from '@seeqdev/ag/grid';

export type { GridState };

/**
 * Default filters for the grid
 * @exposedfortesting
 */
export const defaultFilters = {
  [VantageNames.DerivedColumns.Extinct]: { filterType: 'set', values: ['False'] },
};

interface VantageTableState {
  selection: SimplifiedNode[];
  gridState: GridState;
  quickFilter: string;
  dateRange: ViewDateRange;
  dateTimeRange: any; // any because of Baobab's `monkey()`;
}

export type VantageTableDehydratedState = Omit<VantageTableState, 'selection' | 'dateTimeRange'>;

export class VantageTableStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqVantageTableStore';

  initialize(): void {
    const initialState: VantageTableState = {
      selection: [],
      gridState: { filter: { filterModel: defaultFilters } },
      quickFilter: '',
      dateRange: {
        lookBack: { num: 7, unit: { value: 'day', label: 'day' } },
        lookForward: { num: 0, unit: { value: 'day', label: 'day' } },
        range: [sub(new Date(), { hours: 7 * 24 }), new Date()],
      },
      dateTimeRange: this.monkey(['dateRange'], (dateRange: ViewDateRange) => [
        new Date(dateRange.range?.[0] as Date),
        new Date(dateRange.range?.[1] as Date),
      ]),
    };
    this.state = this.immutable(initialState);
  }

  get selection(): SimplifiedNode[] {
    return this.state.get('selection');
  }

  get gridState(): GridState {
    return this.state.get('gridState');
  }

  get quickFilter(): string {
    return this.state.get('quickFilter');
  }

  get dateRange(): ViewDateRange {
    return this.state.get('dateRange');
  }

  get dateTimeRange(): [Date, Date] {
    return this.state.get('dateTimeRange');
  }

  dehydrate(): VantageTableDehydratedState {
    return _.omit(this.state.serialize(), 'selection', 'dateTimeRange');
  }

  rehydrate(dehydratedState: Partial<VantageTableDehydratedState>): void {
    this.state.deepMerge(dehydratedState);
  }

  protected readonly handlers = {
    SET_VANTAGE_SELECTION: (payload: { selection: SimplifiedNode[] }) => this.state.set('selection', payload.selection),
    SET_VANTAGE_GRID_STATE: ({ gridState }: { gridState: GridState }) => {
      const filterModel = { ...defaultFilters, ...gridState.filter?.filterModel };
      this.state.set('gridState', {
        ...gridState,
        filter: { ...gridState.filter, filterModel },
      });
    },
    SET_VANTAGE_QUICK_FILTER: (payload: { quickFilter: string }) => this.state.set('quickFilter', payload.quickFilter),
    SET_VANTAGE_DATE_RANGE: (payload: { dateRange: ViewDateRange }) => this.state.set('dateRange', payload.dateRange),
  };
}
